import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  MatExpansionModule,
  MatExpansionPanel,
} from '@angular/material/expansion';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import {
  AlertCountModel,
  AlertLocationModel,
} from '@components/dashboard/alert-detail/alert-detail.component';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { RouterModule } from '@angular/router';
import { NumeralPipe } from '@pipes/numeral.pipe';
import { LngLatBounds } from 'maplibre-gl';
import {
  SensingLocationViewModel,
  SensorOption,
} from '../alert-map/alert-map.component';
import { addDays, format } from 'date-fns';
import { SourcePage, SOURCE_PAGE_QUERY_PARAM } from '@entities/shipment';

export type ShipmentAlertCountModel = {
  alertMax: number;
  alertMin: number;
  prealertMax: number;
  prealertMin: number;
  alert: number;
  prealert: number;
};

export type ShipmentSensorAlertModel = {
  shipmentId: string;
  shipmentName: string;
  deviceName: string;
  deviceId: string;
  status: string;
  sensor: SensorOption;
  value?: number;
  threshold?: number;
  location: AlertLocationModel;
  eventAt?: string;
  reportedAt?: string;
  eventType?: string;
};

export type ShipmentSensorAlertHistoryViewModel = {
  /** 配送ID */
  shipmentId: string;
  /** 配送名 */
  shipmentName: string;
  /** 出発予定日時 */
  startDatetime?: string;
  /** 出発実績日時 */
  actualStartDatetime?: string;
  /** 到着予定日時 */
  arrivalDatetime?: string;
  /** 到着実績日時 */
  actualArrivalDatetime?: string;
  /** センサーアラート履歴 */
  alertDetails?: ShipmentSensorAlertModel[];
  /** 最新日時 */
  latestEventAt?: string;
};

@Component({
  selector: 'app-shipment-sensor-alert-detail',
  standalone: true,
  imports: [
    CommonModule,
    MatExpansionModule,
    MatIconModule,
    MatSnackBarModule,
    NumeralPipe,
    RouterModule,
  ],
  templateUrl: './shipment-sensor-alert-detail.component.html',
  styleUrl: './shipment-sensor-alert-detail.component.scss',
})
export class ShipmentSensorAlertDetailComponent implements OnChanges {
  @ViewChildren(MatExpansionPanel) panels = new QueryList<MatExpansionPanel>();

  /** アラート一覧 */
  @Input() public alertList: ShipmentSensorAlertHistoryViewModel[] = [];

  /** 配送情報の検索範囲 */
  @Input() public historyFormGroup: FormGroup | undefined;

  /** ローディング中か */
  @Input() public loading = false;

  /** マップ表示範囲 */
  @Input() mapBounds: LngLatBounds | undefined;

  /** 選択されたマーカー情報 */
  @Input() selectedMarker: ShipmentSensorAlertModel | undefined;

  /** 選択されたアラート詳細 */
  @Output() alertDetailChanged = new EventEmitter<ShipmentSensorAlertModel>();

  /** 選択マーカーを変更した際に通知 */
  @Output() markerChanged = new EventEmitter<SensingLocationViewModel>();

  /** フィルターされたアラート一覧 */
  public filteredAlertList: ShipmentSensorAlertHistoryViewModel[] = [];

  /** 選択されたアラート詳細 */
  public selectedSensorAlertDetail:
    | ShipmentSensorAlertHistoryViewModel
    | undefined;

  /** デバイス毎の各アラートの回数 */
  public alertCounts: { [key: string]: AlertCountModel } = {};

  private readonly sensorNameMap: { [key: string]: string } = {
    temperature: '温度',
    humidity: '湿度',
    pressure: '気圧',
    light: '照度',
    tilt: '傾斜',
    fallDetected: '落下検知',
    mcdDetected: '動作検知',
    lightExposure: '明暗検知',
    shockDetected: '衝撃検知',
  };

  constructor(private snackBar: MatSnackBar) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['mapBounds'] !== undefined) {
      const mapBounds = changes['mapBounds'].currentValue;
      this.filteredAlertList = this.filterAlertsByBounds(
        this.alertList,
        mapBounds,
      );
      this.alertCounts = this.createAlertCounts(this.filteredAlertList);
    }
    if (changes['alertList'] !== undefined) {
      this.filteredAlertList = this.alertList;
      this.alertCounts = this.createAlertCounts(this.filteredAlertList);
    } else if (changes['selectedMarker'] !== undefined) {
      if (this.selectedMarker == undefined) {
        this.selectedSensorAlertDetail = undefined;
      } else {
        const selectedMarker = changes['selectedMarker'].currentValue;
        this.filterAlertsByMarker(this.filteredAlertList, selectedMarker);
      }
    }
  }

  /**
   * ステータスのアラート判定をする
   * @param alertsPerDevice デバイス毎のアラート
   * @returns アラートならTrue
   */
  public isAlert(
    alertsPerDevice: ShipmentSensorAlertHistoryViewModel,
  ): boolean {
    const alertCount = this.alertCounts[alertsPerDevice.shipmentId];
    return (
      alertCount &&
      (alertCount.alertMax >= 1 ||
        alertCount.alertMin >= 1 ||
        alertCount.alert >= 1)
    );
  }

  /**
   * イベント検知のアラートであるか
   * @param deviceId デバイスID
   * @returns Trueの場合、イベント検知のアラート
   */
  public isEventAlert(shipmentId: string) {
    const counts = this.alertCounts[shipmentId];
    if (!counts) return;
    return counts.alert > 0 || counts.prealert > 0;
  }

  /**
   * 選択したマーカー情報と一致した要素をクラスを付与する
   * @param alert アラート情報
   * @returns 一致の場合True
   */
  public isSelectedAlert(alert: ShipmentSensorAlertHistoryViewModel) {
    return this.selectedSensorAlertDetail === alert;
  }

  /**
   * アラート一覧をクリック時通知する
   * @param alert アラート情報
   * @param deviceName デバイス名
   * @param deviceId デバイスID
   */
  public selectedAlert(alert: ShipmentSensorAlertModel) {
    if (alert.location?.latitude == null || alert.location?.longitude == null) {
      this.snackBar.open('位置情報がありません。', '閉じる', {
        duration: 5000,
      });
      return;
    }

    // 選択済みアラートを再度クリックしたときは選択済みアラートをundefinedとする。
    // それ以外の場合はクリックしたアラートを選択済みアラートとして保存する。
    if (this.selectedSensorAlertDetail !== alert) {
      this.selectedSensorAlertDetail = alert;
      this.alertDetailChanged.emit({
        ...alert,
      });
    } else {
      this.selectedSensorAlertDetail = undefined;
      this.selectedMarker = undefined;
      this.alertDetailChanged.emit(undefined);
      this.markerChanged.emit(undefined);
    }
  }

  /**
   * マウスオーバー時にポインターと背景色を変更する
   * @param event MouseEvent
   */
  public onMouseEnter(event: MouseEvent) {
    const target = event.target as HTMLElement;
    target.style.cursor = 'pointer';
    target.style.backgroundColor = 'rgba(0, 0, 0, 0.04)';
  }

  /**
   * マウスを離れた際元のスタイルに変更する
   * @param event MouseEvent
   */
  public onMouseLeave(event: MouseEvent) {
    const target = event.target as HTMLElement;
    target.style.cursor = '';
    target.style.backgroundColor = '';
  }

  /**
   * 指定デバイスのアラート回数を取得する
   * @param deviceId デバイスID
   * @param key キー
   * @returns 回数
   */
  public deviceAlertCount(
    shipmentId: string,
    key: keyof AlertCountModel,
  ): number {
    const alertCount = this.alertCounts[shipmentId];
    return alertCount ? alertCount[key] : 0;
  }

  /**
   * 配送情報画面へのリンクパラメータを取得する
   *
   * @param alertHistory 配送センサーアラート履歴
   * @returns 配送情報画面へのリンクパラメータ
   */
  public getShipmentInfoLinkPrams(
    alertHistory: ShipmentSensorAlertHistoryViewModel,
  ): { [key: string]: string | string[] } {
    const queryParams: { [key: string]: string | string[] } = {
      [SOURCE_PAGE_QUERY_PARAM]: SourcePage.DASHBOARD,
      shipmentId: alertHistory.shipmentId,
      shipmentName: alertHistory.shipmentName,
    };

    const resultHistoryFrom =
      alertHistory.actualStartDatetime ?? alertHistory.startDatetime;
    const resultHistoryTo =
      alertHistory.actualArrivalDatetime ?? alertHistory.arrivalDatetime;
    if (resultHistoryFrom && resultHistoryTo) {
      queryParams['resultHistoryFrom'] = resultHistoryFrom;
      queryParams['resultHistoryTo'] = resultHistoryTo;
    }

    // 出発予定が遅れる可能性も考慮して最大幅まで指定
    if (alertHistory.startDatetime) {
      const startDate = format(alertHistory.startDatetime, 'yyyy-MM-dd');
      const endDate = format(addDays(startDate, 7), 'yyyy-MM-dd');
      queryParams['startDateRange'] = [startDate, endDate];
    }

    return queryParams;
  }

  /**
   * センター名を取得する
   *
   * @param sensorType センサータイプ
   * @returns センター名
   */
  public getSensorName(sensorType: string, eventType?: string) {
    let sensorName = this.sensorNameMap[sensorType] ?? '';
    if (sensorType === 'lightExposure') {
      switch (eventType) {
        case 'LightToDark':
          sensorName = `明 → 暗 検出`;
          break;
        case 'DarkToLight':
          sensorName = `暗 → 明 検出`;
          break;
        case 'LightTooBright':
          sensorName = `明暗設定失敗`;
          break;
        case 'OneTimeLightExposureDetected':
          sensorName = `光検出`;
          break;
      }
    }
    return sensorName;
  }

  /**
   * マーカー選択時にアラート情報一覧をフィルターする
   * @param alertList アラート情報
   * @param selectedMarker 選択マーカー
   */
  private filterAlertsByMarker(
    alertList: ShipmentSensorAlertHistoryViewModel[],
    selectedMarker: ShipmentSensorAlertModel,
  ) {
    alertList.forEach((device) => {
      device.alertDetails?.forEach((alert) => {
        if (
          alert.location.latitude === selectedMarker.location.latitude &&
          alert.location.longitude === selectedMarker.location.longitude &&
          alert.eventAt === selectedMarker.eventAt
        ) {
          this.selectedSensorAlertDetail = alert;
          const alertDetailId = this.createAlertId(alert);
          this.updatePanels(device.shipmentId, alertDetailId);
        }
      });
    });
  }

  /**
   * トレース値を返す
   *
   * @param _ インデックス番号
   * @param alert アラート詳細
   * @returns ユニークになる値
   */
  public trackByAlert(_: number, alert: ShipmentSensorAlertModel) {
    return this.createAlertId(alert);
  }

  /**
   * ユニーク値を返す
   *
   * @param alert アラート詳細
   * @returns ユニークになる値
   */
  public createAlertId(alert: ShipmentSensorAlertModel) {
    return `${alert.deviceId}-${alert.sensor?.label}-${alert.eventAt}`;
  }

  /**
   * パネルの状態を変更する
   * @param shipmentId 配送ID
   * @param alertDetailId イベント発生日時
   */
  private updatePanels(shipmentId: string, alertDetailId: string) {
    this.panels.forEach((panel) => {
      const panelId = panel._body.nativeElement.parentElement?.id;

      if (panelId === shipmentId) {
        // パネルが既に開いているかを確認
        if (panel.expanded) {
          // パネルが既に開いている場合は直接スクロール
          setTimeout(() => {
            document
              .getElementById(alertDetailId)
              ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 100);
        } else {
          // パネルが閉じている場合はafterExpandイベントでスクロール
          const scrollToElement = () => {
            setTimeout(() => {
              document
                .getElementById(alertDetailId)
                ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            });
          };

          // パネルが完全に展開された後にイベントを登録
          const subscription = panel.afterExpand.subscribe(() => {
            scrollToElement();
            subscription.unsubscribe();
          });
          panel.open();
        }
      } else {
        panel.close();
      }
    });
  }

  /**
   * 指定された範囲内のアラートをフィルタリングし集計する
   * @param alertList アラートの一覧
   * @param bounds マップ表示範囲
   * @returns フィルタリングされたアラートの一覧
   */
  private filterAlertsByBounds(
    alertList: ShipmentSensorAlertHistoryViewModel[],
    bounds: LngLatBounds,
  ): ShipmentSensorAlertHistoryViewModel[] {
    const filteredList: ShipmentSensorAlertHistoryViewModel[] = [];

    alertList.forEach((device) => {
      // マップ範囲に存在するアラート詳細を取得
      const filteredDetails = device.alertDetails?.filter((alert) => {
        // 位置情報がないアラートの場合、マップ範囲に関係なく常に表示する
        // 位置情報がある場合、マップ範囲にあるものに絞る
        const noAlertLocation =
          alert.location.longitude == null || alert.location.latitude == null;
        const isWithinBounds =
          alert.location.longitude >= bounds._sw.lng &&
          alert.location.longitude <= bounds._ne.lng &&
          alert.location.latitude >= bounds._sw.lat &&
          alert.location.latitude <= bounds._ne.lat;
        return noAlertLocation || isWithinBounds;
      });

      if (filteredDetails && filteredDetails.length > 0) {
        filteredList.push({
          ...device,
          alertDetails: filteredDetails,
        });
      }
    });

    filteredList.sort(this.compareAlert);

    return filteredList;
  }

  private createAlertCounts(
    deviceAlertList: ShipmentSensorAlertHistoryViewModel[],
  ) {
    const alertCounts: { [key: string]: AlertCountModel } = {};

    for (const deviceAlert of deviceAlertList) {
      // アラートごとの数
      const newAlertCounts: AlertCountModel = {
        alertMax: 0,
        alertMin: 0,
        prealertMax: 0,
        prealertMin: 0,
        alert: 0,
        prealert: 0,
      };

      deviceAlert.alertDetails?.forEach((alert) => {
        this.updateCountSummary(newAlertCounts, alert.status);
      });

      alertCounts[deviceAlert.shipmentId] = newAlertCounts;
    }

    return alertCounts;
  }

  private compareAlert(
    a: ShipmentSensorAlertHistoryViewModel,
    b: ShipmentSensorAlertHistoryViewModel,
  ): number {
    const aName = a.shipmentName;
    const bName = b.shipmentName;
    if (aName === bName) {
      return 0;
    } else {
      return aName > bName ? 1 : -1;
    }
  }

  /**
   * アラートのステータスに基づいてカウントサマリーを更新する
   * @param countSummary 集計モデル
   * @param status アラートのステータス
   */
  private updateCountSummary(
    countSummary: AlertCountModel,
    status: string,
  ): void {
    switch (status) {
      case 'AlertMax':
        countSummary.alertMax += 1;
        break;
      case 'AlertMin':
        countSummary.alertMin += 1;
        break;
      case 'PreAlertMax':
        countSummary.prealertMax += 1;
        break;
      case 'PreAlertMin':
        countSummary.prealertMin += 1;
        break;
      case 'Alert':
        countSummary.alert += 1;
        break;
    }
  }
}
